import React, { useEffect, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Button, Col, Row, Spin, Tooltip, notification } from "antd";
import { CreditCardOutlined, EditOutlined } from "@ant-design/icons";
import CurrentSubscription from "./CurrentSubscription";
import BasicModal from "../../ui/BasicModal/BasicModal";
import CheckoutForm from "../../pages/auth/payment/StripeForm";
import { STRIPE_ELEMENTS_OPTIONS } from "../../pages/auth/payment/stripeElements";
import { httpService } from "../../base/services/httpService.service";
import AuthService from "../../base/services/authentication.service";
import CancelSubscription from "./CancelSubscription";
import BillingDetailsPanel from "../Billing/BillingDetailsPanel";
import CardSetup from "../Billing/CardSetup";
import SubscriptionBillingNotices, {
   PendingPlanChangeNotice
} from "../Billing/SubscriptionBillingNotices";
import {
   describeSavedCard,
   isCancellationScheduled,
   isCommittedPastThisPeriod,
   oldFlowCommitment,
   scheduledEndOf
} from "../Billing/billingActions";
import { billingApi } from "../Billing/billingApi";
import { SubscriptionStatus } from "../Billing/billingCodes";
import { formatBillingDate } from "../Billing/billingDates";
import { isBillingManaged, useBillingStore } from "../Billing/billingStore";
import { formatCardBrand, formatCardExpiry } from "../Billing/cards";
import { isPlanDowngradesEnabled } from "../Billing/planDowngrades";
import "./index.scss";

// Built once, outside the component: a Stripe object rebuilt on every render is
// a Stripe object the Elements around it have to be rebuilt for too.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLEKEY);

// A subscription that is over. Stripe's statuses come through raw now, and one
// that never got going ends as incomplete_expired rather than canceled.
const ENDED_STATUSES = new Set([SubscriptionStatus.CANCELED, SubscriptionStatus.INCOMPLETE_EXPIRED]);

// Two flows run this page, and the backend says which (components/Billing/
// README.md). With the new billing flow on, the organization's billing status
// says what the subscription is doing — a trial, a payment owed, an end date —
// and a card is saved through a SetupIntent (CardSetup), with billing details
// kept beside it. With it off, the page is what it always was.
const PaymentInformation = React.memo(() => {
   const organizationId = AuthService.getOrganizationId();
   const billingManaged = isBillingManaged(organizationId);
   const billingStatus = useBillingStore((state) => state.statuses[organizationId]);
   const [openModal, setOpenModal] = useState({
      editPayment: false,
      cancelSubscription: false
   });
   const [isLoading, setIsLoading] = useState(true);
   const [plan, setPlan] = useState(false);
   const [stripeSubscriptionData, setStripeSubscriptionData] = useState({
      stripeSubscriptionId: null,
      subscriptionStartsAt: null,
      subscriptionEndsAt: null,
      subscriptionStatus: null,
      planId: null,
      planName: null,
      clientSecret: null,
      price: null,
      subscriptionId: null,
      paymentMethodId: null,
      last4CardDigits: null,
      cancelAt: null,
      cancelledAt: null,
      commitmentEnd: null,
      pendingPlanChange: null
   });

   useEffect(() => {
      setIsLoading(true);
      const subscriptionRequest = httpService.get(
         `/Payment/getStripeSubscriptionByOrganization?organizationId=${organizationId}`,
         (res) => {
            // SessionService.setItem("subscriptionId", res.data.subscriptionId);
            setStripeSubscriptionData(res.data);
         }
      );
      // On the new flow, the status as Stripe has it right now. It lands in the
      // billing store, which is what the page reads it from.
      const statusRequest = billingManaged
         ? billingApi.getBillingStatus(organizationId)
         : Promise.resolve();

      Promise.allSettled([subscriptionRequest, statusRequest]).finally(() => {
         setIsLoading(false);
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [plan]);

   const handlePlan = () => {
      setPlan(!plan);
   };

   // A card saved through the new flow. Whatever it settled — a trial now
   // covered, a paused subscription resumed — is read back with the rest.
   //
   // The card is on file even when the bank then turned down the payment it was
   // asked to make, so the form closes either way, and the refusal is said in a
   // notice. Not in the form: a payment waiting on the bank can hold the
   // organization and take it to the billing page mid-flow, and by the time the
   // bank answers, this page may be gone.
   //
   // A card saved with no subscription left for it to pay (Stripe no longer has
   // it) holds the organization for a plan, so the routes take it to the
   // billing page's plans, and the notice says a plan comes next.
   const handleCardSaved = (status, { error } = {}) => {
      setOpenModal({ editPayment: false });

      if (error) {
         notification.error({
            message: "Oops! A problem has occurred!",
            description: "Your card was saved, but your bank didn't approve the payment, so it's still due."
         });
      } else {
         notification.success({
            message: "Card saved successfully",
            description: describeSavedCard(status)
         });
      }

      handlePlan();
   };

   // The card on file. The new flow's status names the one Stripe will really
   // charge; the subscription read names it too now, brand included. One that
   // comes without its last four digits is not described at all.
   const statusCard = billingManaged ? billingStatus?.paymentMethod : null;
   const subscriptionCard = stripeSubscriptionData.paymentMethodId
      ? {
           brand: stripeSubscriptionData.brand,
           last4: stripeSubscriptionData.last4CardDigits,
           expMonth: stripeSubscriptionData.expMonth,
           expYear: stripeSubscriptionData.expYear
        }
      : null;
   const card = [statusCard, subscriptionCard].find((candidate) => candidate?.last4) ?? null;
   const cardExpiry = formatCardExpiry(card?.expMonth, card?.expYear);
   // A card can be on file with nothing yet to name it by: an old customer's,
   // until the backend has synced it (hasPaymentMethod, paymentMethod null). It
   // is still the card that gets charged, so it is not asked for again.
   const hasCardOnFile =
      Boolean(card) ||
      Boolean(billingManaged && billingStatus?.hasPaymentMethod) ||
      Boolean(stripeSubscriptionData.paymentMethodId);

   // The plan boxes' period and state. On the new flow the billing status is
   // where both come from, for every plan, Enterprise included; the
   // subscription read stands in when there is no status. A read that fails —
   // 400 "Plan details could not be found." for a price no plan has — leaves
   // its fields empty, which nothing on the new flow depends on.
   const subscriptionStatus =
      (billingManaged && billingStatus?.subscriptionStatus) || stripeSubscriptionData.subscriptionStatus;
   const isMonthly =
      billingManaged && typeof billingStatus?.isMonthly === "boolean"
         ? billingStatus.isMonthly
         : stripeSubscriptionData.isMonthly;

   // The date under "Next payment due" and its label. The new flow knows a
   // trial's end apart from a renewal; the flow before it had one end date.
   // Either way the dates are UTC and read as such (billingDates.js).
   //
   // A cancellation ends the subscription at `cancelAt`. On an annual plan,
   // billed monthly, that is the end of its 12-month commitment, while the
   // period's end is only the end of the month: so the period's end stands in
   // only when there is no `cancelAt`. Kept, the subscription shows its next
   // payment again, and an annual plan when its commitment runs until.
   const nextPayment = (() => {
      if (billingManaged && billingStatus) {
         if (isCancellationScheduled(billingStatus)) {
            return { label: "Active until", date: formatBillingDate(scheduledEndOf(billingStatus)) };
         }
         if (billingStatus.subscriptionStatus === SubscriptionStatus.TRIALING) {
            return { label: "Free trial ends on", date: formatBillingDate(billingStatus.trialEnd) };
         }
         const commitmentEnd = formatBillingDate(billingStatus.commitmentEnd);
         return {
            label: "Next payment due",
            date: formatBillingDate(
               billingStatus.currentPeriodEnd ?? stripeSubscriptionData.subscriptionEndsAt
            ),
            note: commitmentEnd ? `Annual commitment until ${commitmentEnd}` : null
         };
      }

      // Stripe's cancel_at is set only while a cancellation is scheduled, and
      // on an annual plan cancelled mid-year it is the end of the year, while
      // subscriptionEndsAt is still the end of the month.
      if (stripeSubscriptionData.cancelAt) {
         return {
            label: ENDED_STATUSES.has(stripeSubscriptionData.subscriptionStatus)
               ? "Subscription ends on"
               : "Active until",
            date: formatBillingDate(stripeSubscriptionData.cancelAt)
         };
      }
      const commitmentEnd = formatBillingDate(stripeSubscriptionData.commitmentEnd);
      return {
         label: "Next payment due",
         date: formatBillingDate(stripeSubscriptionData.subscriptionEndsAt),
         note: commitmentEnd ? `Annual commitment until ${commitmentEnd}` : null
      };
   })();

   return (
      <>
         {isLoading ? (
            <Spin
               style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "50px",
                  width: "100%"
               }}
            />
         ) : (
            <div className="paymentInformation accountSettings">
               {/* {stripeSubscriptionData.subscriptionStatus !== "canceled" &&
                  !stripeSubscriptionData.cancelAt && ( */}
               {!ENDED_STATUSES.has(stripeSubscriptionData.subscriptionStatus) && (
                  <Row gutter={[16, 16]} justify="space-between" align="middle">
                     <Col xs={24} sm={18} md={12} lg={12} xl={12}>
                        <span className="title">Payment information</span>
                     </Col>
                     <Col xs={24} sm={6} md={12} lg={12} xl={12} className="cancelSubscription">
                        {billingManaged ? (
                           // Set to end already: the notice below offers to keep
                           // it instead of a button that can do nothing.
                           !isCancellationScheduled(billingStatus) && (
                              <Button
                                 type="default"
                                 className="defaultButton"
                                 onClick={() => setOpenModal({ cancelSubscription: true })}
                              >
                                 Cancel my subscription
                              </Button>
                           )
                        ) : stripeSubscriptionData.cancelAt ? (
                           <Tooltip title="You have already canceled this subscription.">
                              <Button
                                 type="default"
                                 className="defaultButton disabledButton"
                                 onClick={() => setOpenModal({ cancelSubscription: true })}
                                 disabled={true}
                              >
                                 Cancel my subscription
                              </Button>
                           </Tooltip>
                        ) : (
                           <Button
                              type="default"
                              className="defaultButton"
                              onClick={() => setOpenModal({ cancelSubscription: true })}
                              disabled={false}
                           >
                              Cancel my subscription
                           </Button>
                        )}
                     </Col>
                  </Row>
               )}
               {billingManaged && (
                  <SubscriptionBillingNotices
                     organizationId={organizationId}
                     status={billingStatus}
                     onAddCard={() => setOpenModal({ editPayment: true })}
                     onChanged={handlePlan}
                  />
               )}
               {/* The old flow's change waiting for the end of the contract,
                   from its subscription read. */}
               {!billingManaged &&
                  isPlanDowngradesEnabled() &&
                  !stripeSubscriptionData.cancelAt &&
                  stripeSubscriptionData.pendingPlanChange?.packetId && (
                     <PendingPlanChangeNotice
                        organizationId={organizationId}
                        status={{ pendingPlanChange: stripeSubscriptionData.pendingPlanChange }}
                        onChanged={handlePlan}
                        rememberStatus={false}
                     />
                  )}
               <CurrentSubscription
                  planName={stripeSubscriptionData.planName}
                  subscriptionStatus={subscriptionStatus}
                  subscriptionId={stripeSubscriptionData.subscriptionId}
                  price={stripeSubscriptionData.price}
                  isMonthly={isMonthly}
                  subscriptionStartsAt={stripeSubscriptionData.subscriptionStartsAt}
                  commitmentEnd={stripeSubscriptionData.commitmentEnd}
                  subscriptionEndsAt={stripeSubscriptionData.subscriptionEndsAt}
                  cancelAt={stripeSubscriptionData.cancelAt}
                  pendingPlanChange={stripeSubscriptionData.pendingPlanChange}
                  billingManaged={billingManaged}
                  onPlanChanged={handlePlan}
                  // handleUpdatePlan={handlePlan}
               ></CurrentSubscription>
               <div className="paymentMethod">
                  <div className="nextPayment">
                     <p>{nextPayment.label}</p>
                     <h3>{nextPayment.date}</h3>
                     {nextPayment.note && <span className="commitmentNote">{nextPayment.note}</span>}
                  </div>
                  <div className="editPaymentMethod">
                     <p>Payment method</p>
                     <div className="cardInformation">
                        <div className="infoDetails">
                           <div className="creditCardIcon">
                              <CreditCardOutlined
                                 style={{
                                    strokeWidth: "35",
                                    stroke: "white"
                                 }}
                              />
                           </div>
                           <div className="visaEnding">
                              {card ? (
                                 <>
                                    <span>Credit card</span>
                                    <span>
                                       {formatCardBrand(card.brand)} ending in ****{card.last4}
                                       {cardExpiry ? ` · expires ${cardExpiry}` : ""}
                                    </span>
                                 </>
                              ) : hasCardOnFile ? (
                                 <>
                                    <span>Credit card</span>
                                    <span>Card on file</span>
                                 </>
                              ) : (
                                 <span>No card details added</span>
                              )}
                           </div>
                        </div>
                        <Button
                           type="default"
                           className="defaultButton"
                           icon={<EditOutlined />}
                           onClick={() => setOpenModal({ editPayment: true })}
                        >
                           {hasCardOnFile ? (
                              <span>Edit payment method</span>
                           ) : (
                              <span>Add payment method</span>
                           )}
                        </Button>
                     </div>
                  </div>
               </div>
               {billingManaged && <BillingDetailsPanel organizationId={organizationId} />}
               {openModal.editPayment && (
                  <BasicModal
                     isModalOpen={openModal.editPayment}
                     handleCancel={() => setOpenModal({ editPayment: false })}
                  >
                     {billingManaged ? (
                        // Through a SetupIntent, so the bank can check the card as
                        // it is saved, with the agreement to charge it recorded.
                        <CardSetup
                           organizationId={organizationId}
                           status={billingStatus}
                           submitLabel={hasCardOnFile ? "Update card" : "Save card"}
                           onCancel={() => setOpenModal({ editPayment: false })}
                           onDone={handleCardSaved}
                        />
                     ) : (
                        // The flow before the new one. No client secret in the way
                        // of this: adding a card files a payment method against the
                        // organization — it charges nothing — and an account still
                        // inside its free trial has no payment intent to carry a
                        // secret, which is exactly the account that comes here to
                        // put a card on file before the trial ends.
                        <Elements options={STRIPE_ELEMENTS_OPTIONS} stripe={stripePromise}>
                           <CheckoutForm
                              submitLabel={hasCardOnFile ? "Update payment method" : "Add payment method"}
                              handleOk={() => setOpenModal({ editPayment: false })}
                              handleCardDetails={handlePlan}
                           />
                        </Elements>
                     )}
                  </BasicModal>
               )}
               {openModal.cancelSubscription && (
                  <CancelSubscription
                     isModalOpen={openModal.cancelSubscription}
                     handleAccountModal={() => setOpenModal({ cancelSubscription: false })}
                     subscriptionId={stripeSubscriptionData.subscriptionId}
                     handleCancelPlan={handlePlan}
                     billingManaged={billingManaged}
                     billingStatus={billingStatus}
                     commitmentEnd={
                        isCommittedPastThisPeriod(oldFlowCommitment(stripeSubscriptionData))
                           ? stripeSubscriptionData.commitmentEnd
                           : null
                     }
                     pendingPlanChange={
                        isPlanDowngradesEnabled()
                           ? billingManaged
                              ? billingStatus?.pendingPlanChange
                              : stripeSubscriptionData.pendingPlanChange
                           : null
                     }
                  ></CancelSubscription>
               )}
            </div>
         )}
      </>
   );
});

export default PaymentInformation;
